import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import PurchaseOrderService from "services/PurchaseOrderService";
import Utils from "utils";
import openNotification from "utils/notification";

export const getPurchaseOrderApi = createAsyncThunk(
  "purchaseOrder/getPurchaseOrderApi",
  async (data, { rejectWithValue }) => {
    try {
      const response = await PurchaseOrderService.getPurchaseOrder(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getPurchaseOrderDetailApi = createAsyncThunk(
  "purchaseOrder/getPurchaseOrderDetailApi",
  async (id, { rejectWithValue }) => {
    try {
      const response = await PurchaseOrderService.getPurchaseOrderDetail(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const addPurchaseOrderApi = createAsyncThunk(
  "purchaseOrder/addPurchaseOrderApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await PurchaseOrderService.createPurchaseOrder(payload);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const addPurchaseOrderFromRequestOrderApi = createAsyncThunk(
  "purchaseOrder/addPurchaseOrderFromRequestOrderApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response =
        await PurchaseOrderService.createPurchaseOrderFromRequestOrder(payload);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const updatePurchaseOrderApi = createAsyncThunk(
  "purchaseOrder/updatePurchaseOrderApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await PurchaseOrderService.updatePurchaseOrder(payload);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const markPurchaseOrderPurchasingApi = createAsyncThunk(
  "purchaseOrder/markPurchaseOrderPurchasingApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, id } = data;

      const response = await PurchaseOrderService.markPurchasing(id);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const updatePurchaseOrderPaymentStatusApi = createAsyncThunk(
  "purchaseOrder/updatePurchaseOrderPaymentStatusApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await PurchaseOrderService.updatePaymentStatus(payload);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const closeShortPurchaseOrderApi = createAsyncThunk(
  "purchaseOrder/closeShortPurchaseOrderApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response =
        await PurchaseOrderService.closeShortPurchaseOrder(payload);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const delPurchaseOrderApi = createAsyncThunk(
  "purchaseOrder/delPurchaseOrderApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, id } = data;

      const response = await PurchaseOrderService.delPurchaseOrder(id);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const addPurchaseOrderAttachmentApi = createAsyncThunk(
  "purchaseOrder/addPurchaseOrderAttachmentApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, ...payload } = data;

      const response = await PurchaseOrderService.addAttachment(payload);

      if (onSuccess) onSuccess(response);

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const delPurchaseOrderAttachmentApi = createAsyncThunk(
  "purchaseOrder/delPurchaseOrderAttachmentApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, id } = data;

      const response = await PurchaseOrderService.delAttachment(id);

      if (onSuccess) onSuccess(response);

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const recalculatePurchaseOrderProgressApi = createAsyncThunk(
  "purchaseOrder/recalculatePurchaseOrderProgressApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, id } = data;

      const response = await PurchaseOrderService.recalculateProgress(id);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const previewPurchaseOrderPricesApi = createAsyncThunk(
  "purchaseOrder/previewPurchaseOrderPricesApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await PurchaseOrderService.previewPrices(payload);
      const previewData = response.data ?? [];
      if (onSuccess) onSuccess(previewData);

      return previewData;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const previewMultiplePurchaseOrdersApi = createAsyncThunk(
  "purchaseOrder/previewMultiplePurchaseOrdersApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await PurchaseOrderService.previewMultipleFromRequestOrders(payload);

      const previewData = response.data ?? {};
      if (onSuccess) onSuccess(previewData);

      return previewData;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const createMultiplePurchaseOrdersApi = createAsyncThunk(
  "purchaseOrder/createMultiplePurchaseOrdersApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await PurchaseOrderService.createMultipleFromRequestOrders(payload);

      const result = response.data ?? {};
      if (onSuccess) onSuccess(result);

      return result;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const exportPurchaseOrderExcel = createAsyncThunk(
  "purchaseOrder/exportExcel",
  async (params, { rejectWithValue }) => {
    try {
      const blob = await PurchaseOrderService.exportExcel(params);
      const url = window.URL.createObjectURL(blob);
      const filename = `Danh sách phiếu đặt hàng.xlsx`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

const initialState = {
  loading: false,
  loadingExport: false,
  
  purchaseOrderList: [],
  purchaseOrderDetail: {},

  purchaseOrderPricePreview: [],
  previewPricesLoading: false,

  multiplePreview: null,
  multiplePreviewLoading: false,
  createMultipleLoading: false,

  totalCount: 0,

  currentRequestId: undefined,
};

export const purchaseOrderSlice = createSlice({
  name: "purchaseOrder",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.loading = true;
    },
    setPurchaseOrderDetail: (state, action) => {
      state.purchaseOrderDetail = action.payload || {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPurchaseOrderApi.pending, (state, action) => {
        state.loading = true;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(getPurchaseOrderApi.fulfilled, (state, action) => {
        state.loading = false;
        state.purchaseOrderList = action.payload.data;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(getPurchaseOrderApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(getPurchaseOrderDetailApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPurchaseOrderDetailApi.fulfilled, (state, action) => {
        state.loading = false;
        state.purchaseOrderDetail = action.payload;
      })
      .addCase(getPurchaseOrderDetailApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(addPurchaseOrderApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(addPurchaseOrderApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addPurchaseOrderApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(addPurchaseOrderFromRequestOrderApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(addPurchaseOrderFromRequestOrderApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addPurchaseOrderFromRequestOrderApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(updatePurchaseOrderApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePurchaseOrderApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updatePurchaseOrderApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(markPurchaseOrderPurchasingApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(markPurchaseOrderPurchasingApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(markPurchaseOrderPurchasingApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(updatePurchaseOrderPaymentStatusApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePurchaseOrderPaymentStatusApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updatePurchaseOrderPaymentStatusApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(closeShortPurchaseOrderApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(closeShortPurchaseOrderApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(closeShortPurchaseOrderApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(delPurchaseOrderApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(delPurchaseOrderApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(delPurchaseOrderApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(recalculatePurchaseOrderProgressApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(recalculatePurchaseOrderProgressApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(recalculatePurchaseOrderProgressApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(previewPurchaseOrderPricesApi.pending, (state) => {
        state.previewPricesLoading = true;
      })
      .addCase(previewPurchaseOrderPricesApi.fulfilled, (state, action) => {
        state.previewPricesLoading = false;
        state.purchaseOrderPricePreview = action.payload;
      })
      .addCase(previewPurchaseOrderPricesApi.rejected, (state) => {
        state.previewPricesLoading = false;
      })

      .addCase(previewMultiplePurchaseOrdersApi.pending, (state) => {
        state.multiplePreviewLoading = true;
      })
      .addCase(previewMultiplePurchaseOrdersApi.fulfilled, (state, action) => {
        state.multiplePreviewLoading = false;
        state.multiplePreview = action.payload;
      })
      .addCase(previewMultiplePurchaseOrdersApi.rejected, (state) => {
        state.multiplePreviewLoading = false;
      })

      .addCase(createMultiplePurchaseOrdersApi.pending, (state) => {
        state.createMultipleLoading = true;
      })
      .addCase(createMultiplePurchaseOrdersApi.fulfilled, (state) => {
        state.createMultipleLoading = false;
      })
      .addCase(createMultiplePurchaseOrdersApi.rejected, (state) => {
        state.createMultipleLoading = false;
      })
      
      .addCase(exportPurchaseOrderExcel.pending, (state) => {
        state.loadingExport = true;
      })
      .addCase(exportPurchaseOrderExcel.fulfilled, (state) => {
        state.loadingExport = false;
      })
      .addCase(exportPurchaseOrderExcel.rejected, (state) => {
        state.loadingExport = false;
      });
  },
});

export const { showLoading, setPurchaseOrderDetail } =
  purchaseOrderSlice.actions;

export default purchaseOrderSlice.reducer;